import { useEffect, useState } from "react";
import { GUIDES, cityEn, guideChips, paletteFor } from "@lantern/core";
import type { Experience } from "@lantern/core";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "3-6,9-11" -> "Mar–Jun, Sep–Nov". Year-round stays year-round. */
function seasonLabel(months: string): { en: string; cn: string } {
  if (!months || months === "全年") return { en: "Year-round", cn: "全年" };
  const parts = months.split(",").map((r) => {
    const [a, b] = r.split("-").map(Number);
    if (!b) return { en: MONTHS[a - 1]!, cn: `${a}月` };
    return { en: `${MONTHS[a - 1]}–${MONTHS[b - 1]}`, cn: `${a}–${b}月` };
  });
  return { en: parts.map((p) => p.en).join(", "), cn: parts.map((p) => p.cn).join("、") };
}

function durationLabel(days: number): { en: string; cn: string } {
  if (days <= 0.35) return { en: "2–3 hours", cn: "2–3 小时" };
  if (days <= 0.5) return { en: "Half a day", cn: "半天" };
  if (days <= 0.7) return { en: "Most of a day", cn: "大半天" };
  return { en: "A full day", cn: "一整天" };
}

function kidsLabel(family: number, minAge?: number): { en: string; cn: string } {
  if (minAge) return { en: `Ages ${minAge}+`, cn: `建议 ${minAge} 岁以上` };
  if (family >= 0.85) return { en: "Great with kids", cn: "很适合带孩子" };
  if (family >= 0.5) return { en: "Fine with kids", cn: "可以带孩子" };
  if (family >= 0.2) return { en: "Not really for kids", cn: "不太适合孩子" };
  return { en: "Adults only", cn: "不适合孩子" };
}

const Row = ({ label, en, cn }: { label: string; en: string; cn: string }) => (
  <div
    className="flex justify-between items-baseline gap-3 py-2"
    style={{ borderTop: "1px solid #1E2236" }}
  >
    <span
      style={{
        fontSize: 10,
        letterSpacing: ".09em",
        textTransform: "uppercase",
        color: "#6E7290",
        flexShrink: 0,
      }}
    >
      {label}
    </span>
    <span className="text-right" style={{ fontSize: 12.5, color: "#CFCBBF", lineHeight: 1.45 }}>
      {en}
      <span style={{ color: "#6E7290" }}> · {cn}</span>
    </span>
  </div>
);

/**
 * Full detail for one experience, as a sheet over whatever screen you were on.
 *
 * The catalog and guide data already held duration, cost, seasonality and age
 * suitability; none of it was ever shown. This is where it surfaces.
 */
export default function ExperienceDetail({
  exp,
  onClose,
  sidekickNote,
}: {
  exp: Experience;
  onClose: () => void;
  sidekickNote?: string;
}) {
  const g = GUIDES[exp.id];
  const chips = guideChips(g);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [grad] = useState(() => paletteFor(exp));

  useEffect(() => {
    const img = new Image();
    img.onload = () => setHasPhoto(true);
    img.src = `/${exp.id}.jpg`;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [exp.id, onClose]);

  const season = seasonLabel(exp.months);
  const dur = durationLabel(exp.days);
  const kids = kidsLabel(exp.family, g?.minAge);

  return (
    <div
      className="absolute inset-0 z-50 flex flex-col"
      style={{ background: "#0A0B14" }}
      role="dialog"
      aria-label={exp.en}
    >
      <div className="relative flex-shrink-0" style={{ height: 210 }}>
        <div
          className="absolute inset-0"
          style={{
            background: hasPhoto
              ? `center/cover no-repeat url(/${exp.id}.jpg)`
              : `linear-gradient(150deg, ${grad[0]}, ${grad[1]} 55%, ${grad[2]})`,
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0"
          style={{ height: 120, background: "linear-gradient(to top, #0A0B14, rgba(10,11,20,0))" }}
        />
        <button
          onClick={onClose}
          className="absolute rounded-full flex items-center justify-center"
          style={{
            top: 12,
            left: 12,
            width: 32,
            height: 32,
            background: "rgba(10,11,20,.6)",
            border: "1px solid rgba(245,242,233,.22)",
            color: "#F5F2E9",
            fontSize: 15,
            backdropFilter: "blur(4px)",
          }}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div
        className="flex-1 overflow-y-auto px-5 pb-6"
        // sits above the hero so the title is not clipped by it
        style={{ marginTop: -46, position: "relative", zIndex: 1 }}
      >
        <div className="text-xs uppercase tracking-widest" style={{ color: "#8A8FA8" }}>
          {cityEn(exp.city)} · {exp.city}
        </div>
        <h2
          style={{
            color: "#F5F2E9",
            fontFamily: "Georgia, serif",
            fontSize: 25,
            lineHeight: 1.15,
            marginTop: 3,
          }}
        >
          {exp.en}
        </h2>
        <div className="text-sm" style={{ color: "#CFCBBF", marginTop: 2 }}>
          {exp.cn}
        </div>

        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {chips.map((c) => (
              <span
                key={c.icon + c.label}
                className="inline-flex items-center gap-1 rounded-full"
                style={{
                  fontSize: 11,
                  lineHeight: 1.7,
                  padding: "2px 9px",
                  color: c.warn ? "#FFD9CC" : "#CFCBBF",
                  background: c.warn ? "rgba(228,87,46,.18)" : "rgba(23,26,43,.9)",
                  border: `1px solid ${c.warn ? "rgba(228,87,46,.5)" : "#2E3350"}`,
                }}
              >
                <span style={{ fontSize: 11, lineHeight: 1.7 }}>{c.icon}</span>
                {c.label}
              </span>
            ))}
          </div>
        )}

        {exp.hook && (
          <p
            style={{
              fontSize: 14,
              color: "#CFCBBF",
              lineHeight: 1.6,
              marginTop: 12,
              fontStyle: "italic",
            }}
          >
            {exp.hook}
          </p>
        )}

        {g?.realityEn && (
          <div
            className="mt-4 rounded-2xl px-3.5 py-3"
            style={{ background: "#12141F", border: "1px solid #2E3350" }}
          >
            <div
              style={{
                fontSize: 10,
                letterSpacing: ".09em",
                textTransform: "uppercase",
                color: "#E0B14F",
              }}
            >
              What the photos don't say · 照片没告诉你的
            </div>
            <p style={{ fontSize: 12.5, color: "#CFCBBF", lineHeight: 1.6, marginTop: 6 }}>
              {g.realityEn}
            </p>
            <p style={{ fontSize: 12.5, color: "#8A8FA8", lineHeight: 1.6, marginTop: 4 }}>
              {g.realityCn}
            </p>
          </div>
        )}

        {g?.tipsEn?.length ? (
          <div className="mt-3">
            <div
              style={{
                fontSize: 10,
                letterSpacing: ".09em",
                textTransform: "uppercase",
                color: "#6E7290",
              }}
            >
              Before you go · 出发前须知
            </div>
            {g.tipsEn.map((t, i) => (
              <div
                key={t}
                className="flex gap-2 mt-1.5"
                style={{ fontSize: 12.5, lineHeight: 1.55 }}
              >
                <span style={{ color: "#E4572E" }}>·</span>
                <span>
                  <span style={{ color: "#CFCBBF" }}>{t}</span>
                  {g.tipsCn?.[i] && <span style={{ color: "#6E7290" }}> / {g.tipsCn[i]}</span>}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {g?.seasonEn && (
          <div className="mt-3" style={{ fontSize: 12.5, color: "#E0B14F", lineHeight: 1.55 }}>
            📅 {g.seasonEn}
            <span style={{ color: "#6E7290" }}> / {g.seasonCn}</span>
          </div>
        )}

        <div className="mt-4">
          <Row label="When" en={season.en} cn={season.cn} />
          <Row label="How long" en={dur.en} cn={dur.cn} />
          <Row
            label="Cost"
            en={"¥".repeat(Math.max(1, exp.cost))}
            cn={["便宜", "中等", "偏贵"][Math.min(2, exp.cost - 1)]!}
          />
          <Row label="Kids" en={kids.en} cn={kids.cn} />
          {g?.altitude ? (
            <Row
              label="Altitude"
              en={`${g.altitude}m${g.altitudeMax ? ` (up to ${g.altitudeMax}m)` : ""}`}
              cn={`海拔 ${g.altitude} 米`}
            />
          ) : null}
        </div>

        {sidekickNote && (
          <div
            className="mt-4 rounded-2xl px-3.5 py-3"
            style={{ background: "rgba(228,87,46,.08)", border: "1px solid rgba(228,87,46,.3)" }}
          >
            <div
              style={{ fontSize: 12.5, color: "#FFD9CC", lineHeight: 1.55, fontStyle: "italic" }}
            >
              🔑 {sidekickNote}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
